/*
 * The Versions tab of an item's details column (S-01 §3.2; S-06 §3.5, §3.6; mockup M14b), as the
 * `detailsViews` slot takes it. It sits in the group "drive.item" beside Details and Activity, after
 * Details, and answers for a file and for a document opened from one, whose kept uploads are its
 * originals (S-15 §3.9: "the Word file stays as version 1 of this item"); a folder and a document
 * made in teamBase have no versions of this kind.
 *
 * This file is statically reachable from the module object, so it holds the entry and nothing else:
 * the view itself is a chunk (`VersionsView.tsx`).
 */

import { lazy } from "react"
import type { DetailsView } from "../../../core/modules/registry.ts"
import { client } from "../../../core/store/store.ts"
import { driveSlice } from "../reducer.ts"
import { DETAILS_GROUP } from "../state/details.ts"
import { KindDocument, KindFile } from "../wire/drive.gen.ts"

/** The view's id, which a request to open it names. */
export const VERSIONS_VIEW = "drive.versions"

/** Whether the key names a file, or a document with originals, this visit has met. */
function hasVersions(key: string | number | undefined): boolean {
	if (typeof key !== "string") return false
	const items = Object.values(driveSlice(client.api.getState()).items)
	return items.some(
		(item) =>
			item.key === key &&
			(item.kind === KindFile || (item.kind === KindDocument && (item.originals ?? 0) > 0)),
	)
}

export const versionsDetailsView: DetailsView = {
	id: VERSIONS_VIEW,
	titleKey: "drive.versions.title",
	tabLabelKey: "drive.details.tab.versions",
	group: DETAILS_GROUP,
	order: 20,
	View: lazy(async () => ({ default: (await import("./VersionsView.tsx")).VersionsView })),
	relevant: (params) => hasVersions(params.key),
}
